import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Channel, EmailActivityChannelDetails } from '@omni/classes/consent/channel.class';
import { DirectMessagingService } from '@omni/services/direct-messaging/direct-messaging.service';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * OMNI-59412 — dropdown popover anchored under the chevron of the
 * "Create Message" split-button on the Message Templates tool's
 * template-details right pane.
 *
 * Renders a label-only list of allowed channels (intersection of the
 * template's channels and the rep's saved channels), with a trailing
 * checkmark on whichever row matches `defaultChannelId`. Selecting a
 * row dismisses the popover with `{ channel }`. Outside-click /
 * backdrop dismisses with `undefined` (native Ionic behaviour).
 *
 * Row labels match New Activity: `DirectMessagingService.channelList` `title`
 * (including non-native suffix) + `translateNewActivityChannelTitle`, same as
 * `new-activity.html` Message row.
 */
@Component({
  selector: 'template-channel-picker',
  templateUrl: 'template-channel-picker.popover.html',
  styleUrls: ['template-channel-picker.popover.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateChannelPickerPopover implements OnInit, OnChanges, OnDestroy {
  @Input() channels: Channel[] = [];
  @Input() defaultChannelId: string | null = null;

  private readonly destroy$ = new Subject<void>();
  private readonly channels$ = new BehaviorSubject<Channel[]>([]);
  private lastDetailsList: EmailActivityChannelDetails[] = [];
  private titleById = new Map<string, string>();
  /** Same ordering as `NewActivityComponent` Message row: `title` ascending via `localeCompare`. */
  public sortedChannels: Channel[] = [];

  constructor(
    private readonly popoverCtrl: PopoverController,
    public readonly translate: TranslateService,
    private readonly directMessaging: DirectMessagingService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    // Align BS with inputs in case Ionic applies `componentProps` before the first CD
    // that runs `ngOnChanges`; also avoids combineLatest emitting [details, []] when
    // `channelList` fires before `channels$.next` from `ngOnChanges`.
    this.channels$.next(this.channels ?? []);
    combineLatest([this.directMessaging.channelList, this.channels$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([detailsList, chans]) => {
        this.lastDetailsList = detailsList;
        this.rebuildLabelMap(chans, detailsList);
        this.cdr.markForCheck();
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['channels']) {
      this.channels$.next(this.channels ?? []);
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private rebuildLabelMap(chans: Channel[], detailsList: EmailActivityChannelDetails[]): void {
    this.titleById.clear();
    // Prefer @Input — it can be set before `channels$` catches up when `channelList` emits first.
    const list =
      this.channels?.length ? this.channels : (chans ?? []);
    this.sortedChannels = [...list].sort((a, b) =>
      this.sortKeyForChannel(a, detailsList).localeCompare(this.sortKeyForChannel(b, detailsList)),
    );
    for (const c of this.sortedChannels) {
      const id = (c?.indskr_consenttypeid as unknown as string) ?? '';
      this.titleById.set(id, this.directMessaging.getNewActivityStyleLabel(c, detailsList));
    }
  }

  /**
   * Matches `EmailActivityChannelDetails.title` used in new-activity channel list sort
   * (`channelList` → `filteredChannelList` sorted by `title`).
   */
  private sortKeyForChannel(
    channel: Channel,
    detailsList: EmailActivityChannelDetails[],
  ): string {
    const id = (channel?.indskr_consenttypeid as unknown as string) ?? '';
    const det = detailsList.find((d) => d.id === id);
    return det?.title ?? (channel?.indskr_consentType as string) ?? '';
  }

  public select(channel: Channel): void {
    this.popoverCtrl.dismiss({ channel });
  }

  public isDefault(channel: Channel): boolean {
    return !!this.defaultChannelId
      && (channel?.indskr_consenttypeid as unknown as string) === this.defaultChannelId;
  }

  public labelFor(channel: Channel): string {
    const id = (channel?.indskr_consenttypeid as unknown as string) ?? '';
    if (!id) {
      return this.directMessaging.getNewActivityStyleLabel(
        channel,
        this.lastDetailsList,
      );
    }
    return this.titleById.get(id) ?? this.directMessaging.getNewActivityStyleLabel(
      channel,
      this.lastDetailsList,
    );
  }

  public trackByChannelId(_: number, c: Channel): string {
    return (c?.indskr_consenttypeid as unknown as string) ?? '';
  }
}
